import React, { useRef, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { setDraggedFile } from 'Manage/actions'
import * as Sentry from '@sentry/react'
import { Button, FormControl } from 'react-bootstrap'
import { Formik } from 'formik'
import * as yup from 'yup'

import {
  DROPZONE_ACCEPTED_FILES_PRO,
  API_ROOT,
  MAX_FILE_SIZE,
  MAX_PDF_SIZE,
  MIXED_FOLDER_ERROR_MSG,
  MIXED_ZIP_ERROR_MSG
} from '../../constants'
import { getErrorMessage, isWasmerFile } from '../../utils/general'
import SelectedFile from '../SelectedFile/SelectedFile'
import { transferRequestLinkUpdate } from '../../services/site'
import { isZipFile } from '../CreateUpdateSiteModal/interface'
import { isFolderDrop, zipFolderFiles, createMockFolderFile, zipFolderFilesWithWorker } from '../../utils/folder-upload'
import { promptForAdditionalFiles, removeFileFromUploadState } from '../../utils/file-upload'
import HomePageHeader from '../HomePageHeader'
import '../../Landing/Home/Home.css'
import { LoadingBox } from '../LoadingBox/LoadingBox'
import { ErrorBox } from '../ErrorBox/ErrorBox'
import { calculateTotalSize, MAX_DIRECT_UPLOAD_SIZE } from '../../services/create-update-site/pro'
import { isVideoFile } from '../../helpers/file-type'
import { handlePresignedUploads, getFilesMetadata } from '../../services/presigned-uploads/handlePresignedUploads'
import Footer from '../Footer'
import StyledDropzone from '../StyledDropzone'
import SuccessIcon from '../SuccessIcon/SuccessIcon'
import DropzoneWrapper from 'components/DragAndDrop/component/DropzoneWrapper'
import { useUploadStats } from '../../hooks/useUploadStats'
import UploadStats from '../UploadStats/UploadStats'
import toast from 'react-hot-toast'

const getEmailFromToken = () => {
  try {
    const token = localStorage.getItem('token')
    if (!token) return null
    const payloadBase64 = token.split('.')[1]
    if (!payloadBase64) return null
    const decodedPayload = JSON.parse(atob(payloadBase64))
    return decodedPayload?.email || decodedPayload?.sub || null
  } catch (error) {
    console.error('Error decoding JWT token:', error)
    return null
  }
}

const isUserLoggedIn = () => {
  const token = localStorage.getItem('token')
  if (!token) return false
  try {
    const payloadBase64 = token.split('.')[1]
    const decodedPayload = JSON.parse(atob(payloadBase64))
    const currentTime = Date.now() / 1000
    return decodedPayload?.exp && decodedPayload.exp > currentTime
  } catch {
    return false
  }
}

const TransferRequestLinkUpload = () => {
  const { domain } = useParams()
  const formRef = useRef()
  const dispatch = useDispatch()
  const { draggedFile } = useSelector((state) => state.manage)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showLoading, setShowLoading] = useState(false)
  const [showError, setShowError] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { uploadStats, uploadProgress, resetStats } = useUploadStats()
  const [showUploadStats, setShowUploadStats] = useState(false)
  const [currentUserEmail, setCurrentUserEmail] = useState('')
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  useEffect(() => {
    const loggedIn = isUserLoggedIn()
    setIsLoggedIn(loggedIn)

    if (loggedIn) {
      const emailFromToken = getEmailFromToken()
      if (emailFromToken) {
        setCurrentUserEmail(emailFromToken)
      }
    }
  }, [])

  const getValidationSchema = () => {
    if (isLoggedIn) {
      return yup.object({
        fileSelected: yup.mixed().required('Please select a file')
      })
    } else {
      return yup.object({
        email: yup.string().email("Oops, that's not a valid email").required('Please enter an email'),
        fileSelected: yup.mixed().required('Please select a file')
      })
    }
  }

  useEffect(() => {
    const handleBeforeUnload = async (event) => {
      if (showLoading) {
        localStorage.setItem('cancelPresignedUpload', 'true')
        event.preventDefault()
        event.returnValue = ''
        /** Chrome requires returnValue to be set */
        return ''
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  })

  useEffect(() => {
    let isMounted = true

    if (draggedFile) {
      const files = Array.isArray(draggedFile) ? draggedFile : [draggedFile]
      const isReplacement = files.some((f) => f && f.isFolder)

      if (!isReplacement) {
        const existingFiles = formRef.current?.values?.files || []
        const fileSelected = formRef.current?.values?.fileSelected

        const allFiles = [...(fileSelected ? [fileSelected] : []), ...existingFiles, ...files].filter(Boolean)
        if (allFiles.some((file) => file && isZipFile(file.type)) && allFiles.length > 1) {
          if (allFiles.some((file) => file && file.isFolder)) {
            toast.error(MIXED_FOLDER_ERROR_MSG)
          } else {
            toast.error(MIXED_ZIP_ERROR_MSG)
          }
          if (isMounted) {
            dispatch(setDraggedFile(null))
          }
          return
        }
      }

      onFileSelected(files).then(() => {
        if (isMounted) {
          dispatch(setDraggedFile(null))
        }
      })
    }

    return () => {
      isMounted = false
    }
  }, [draggedFile, dispatch])

  const onFileSelected = async (passedFiles) => {
    let files = passedFiles || []
    let setFieldValue = formRef.current.setFieldValue

    if (isFolderDrop(passedFiles)) {
      try {
        const zipFile = createMockFolderFile(passedFiles)
        files = [zipFile]
      } catch (error) {
        toast.error('Error processing the folder. Please try again.')
        return
      }
    }

    if (files.some((file) => file.path?.startsWith('/'))) {
      files = files.filter((file) => !file.path?.startsWith('/'))
    }

    if (files.some((file) => isZipFile(file.type)) && files.length > 1) {
      if (files.some((file) => file.isFolder)) {
        toast.error(MIXED_FOLDER_ERROR_MSG)
      } else {
        toast.error(MIXED_ZIP_ERROR_MSG)
      }
      return
    }

    const selectedFile = files[0]
    const containsWasmerFile = files.some((file) => isWasmerFile(file))
    if (containsWasmerFile) {
      alert('PHP/Python files are not supported for transfer request links.')
      return
    }

    setFieldValue('fileSelected', selectedFile)
    setFieldValue('files', files.slice(1))
  }

  const onSubmit = async ({ fileSelected: initialFileSelected, files, email }) => {
    let fileSelected = initialFileSelected
    if (fileSelected?.isRawFolder) {
      try {
        fileSelected = await zipFolderFilesWithWorker(fileSelected)
      } catch (error) {
        toast.error('Error compressing folder. Please try again.')
        return
      }
    }

    const formData = new FormData()
    const emailToUse = isLoggedIn ? currentUserEmail : email
    formData.append('domain', domain)
    formData.append('uploaderEmail', emailToUse.toLowerCase())

    const allFiles = files.length > 0 ? [fileSelected, ...files] : [fileSelected]

    const totalSize = await calculateTotalSize(allFiles)
    const isVideoFiles = allFiles.some((file) => isVideoFile(file.name, file.type))
    const isPresignedUpload = totalSize > MAX_DIRECT_UPLOAD_SIZE || isVideoFiles

    const MIN_SIZE_FOR_STATS = 2 * 1024 * 1024 * 1024
    setShowUploadStats(totalSize >= MIN_SIZE_FOR_STATS)

    localStorage.removeItem('cancelPresignedUpload')
    setShowLoading(true)
    resetStats()

    try {
      if (isPresignedUpload) {
        const totalFilesMetadata = {
          totalFiles: getFilesMetadata(allFiles),
          completedFiles: []
        }
        const config = { domain, uploaderEmail: emailToUse.toLowerCase() }
        await handlePresignedUploads({
          files: allFiles,
          domain,
          newDomain: domain,
          formData,
          config,
          isUpdate: true,
          isUpdateSite: true,
          uploadProgress: (progress, file) => {
            uploadProgress(progress, { size: file.size }, true)
          },
          isLastFile: allFiles.length === 1,
          filesToAddPresignedTotalSize: totalSize,
          filesToAddPresignedMetadata: totalFilesMetadata,
          isTransferRequestLink: true,
          uploaderEmail: emailToUse.toLowerCase()
        })
      } else {
        if (files.length > 0) {
          formData.append('filesToAdd', fileSelected)
          files.forEach((file) => formData.append('filesToAdd', file))
        } else {
          formData.append('filesToAdd', fileSelected)
        }
        await transferRequestLinkUpdate({ domain, uploaderEmail: emailToUse.toLowerCase() }, formData, (event) => {
          const { total, loaded } = event
          uploadProgress({ total, loaded }, { size: total })
        })
      }

      setShowLoading(false)
      setShowSuccess(true)
      setShowUploadStats(false)
    } catch (err) {
      setShowLoading(false)
      setShowError(true)
      setShowUploadStats(false)
      const errorMessage = getErrorMessage(err)
      setErrorMessage(errorMessage || 'Oops, something went wrong')
      Sentry.captureException(err, { tags: { section: 'transfer-request-link' } })
    }
  }

  const addMoreFiles = (setFieldValue, files, fileSelected) => {
    promptForAdditionalFiles({
      files,
      fileSelected,
      acceptedFilesType: DROPZONE_ACCEPTED_FILES_PRO,
      onFilesValidated: (validFilesToAdd) => {
        setFieldValue('files', [...files, ...validFilesToAdd])
      }
    })
  }

  const removeFile = (setFieldValue, files, fileSelected, isSelectedFile) => {
    removeFileFromUploadState({
      files,
      fileSelected: formRef.current?.values?.fileSelected,
      fileToRemove: fileSelected,
      isSelectedFile,
      onUpdateFiles: ({ newSelectedFile, newFiles }) => {
        setFieldValue('fileSelected', newSelectedFile)
        setFieldValue('files', newFiles)
      }
    })
  }

  return (
    <DropzoneWrapper acceptedFiles={DROPZONE_ACCEPTED_FILES_PRO}>
      <div className="home">
        <HomePageHeader />
        <div className="mt-5 mb-5">
          <h1 className="font-26 bold mb-2">Upload files to</h1>
          <a
            href={`https://${domain}`}
            target="_blank"
            rel="noopener noreferrer"
            className="subtitle d-block mb-5 text-decoration-none"
          >
            {domain}
          </a>
          <div className="upload-box" style={{ maxWidth: '650px', width: '100%' }}>
            {showLoading ? (
              <div>
                <LoadingBox
                  message="Uploading files..."
                  showProgressBar={uploadStats.progress > 0}
                  progress={uploadStats.progress}
                />
                {showUploadStats && <UploadStats uploadStats={uploadStats} />}
              </div>
            ) : showError ? (
              <ErrorBox
                message={errorMessage}
                setRootState={() => {
                  setShowError(false)
                  setErrorMessage('')
                  setShowUploadStats(false)
                }}
              />
            ) : showSuccess ? (
              <div className="success-box">
                <h3>Success</h3>
                <div className="flex justify-content-center align-items-center p-5">
                  <SuccessIcon />
                </div>
                <div>
                  <h4 className="domain">Your file has been uploaded</h4>
                  <p className="mt-3">We have notified the owner of the files uploaded</p>
                </div>
                <div className="mb-3 mt-4 d-flex justify-content-center">
                  <Button variant="primary" style={{ minWidth: '150px' }} onClick={() => setShowSuccess(false)}>
                    Upload more files
                  </Button>
                </div>
              </div>
            ) : (
              <Formik
                innerRef={formRef}
                validationSchema={getValidationSchema()}
                onSubmit={onSubmit}
                initialValues={{
                  fileSelected: undefined,
                  files: [],
                  email: isLoggedIn ? currentUserEmail : ''
                }}
                enableReinitialize={true}
              >
                {({ handleSubmit, handleChange, values, setFieldValue, touched, errors, isSubmitting }) => (
                  <form onSubmit={handleSubmit}>
                    {values.fileSelected ? (
                      <>
                        <div className={`${values.files.length > 0 ? 'file-list' : ''}`}>
                          <SelectedFile
                            file={values.fileSelected}
                            onRemove={() => removeFile(setFieldValue, values.files, values.fileSelected, true)}
                            hideHomepageLabel={true}
                          />
                          {values.files.map((file, index) => (
                            <SelectedFile
                              key={index}
                              file={file}
                              onRemove={() => removeFile(setFieldValue, values.files, file, false)}
                              hideHomepageLabel={true}
                            />
                          ))}
                        </div>
                        {!isZipFile(values.fileSelected?.type) &&
                          ![values.fileSelected, ...values.files].some((f) => f?.isFolder) && (
                            <Button
                              className="mb-3 mt-2 ms-auto d-block"
                              variant="outline-primary"
                              size="sm"
                              onClick={() => addMoreFiles(setFieldValue, values.files, values.fileSelected)}
                            >
                              + Add more files
                            </Button>
                          )}

                        {!isLoggedIn && (
                          <>
                            <div className="mb-3 mt-4">
                              <div className="bold font-20">Please enter your email</div>
                              <span className="light-grey font-small">
                                So we can notify the owner who uploaded the files
                              </span>
                            </div>
                            <div className="d-flex align-items-start gap-2">
                              <div className="flex-grow-1">
                                <FormControl
                                  className="tr-landing-email-input"
                                  name="email"
                                  type="email"
                                  placeholder="Email"
                                  aria-label="Email"
                                  value={values.email}
                                  onChange={handleChange}
                                  isInvalid={touched.email && errors.email}
                                />
                                {touched.email && errors.email && (
                                  <div className="validation-error mt-2">{errors.email}</div>
                                )}
                              </div>
                              <Button
                                type="submit"
                                variant="outline-primary"
                                className="min-w-150"
                                disabled={isSubmitting}
                              >
                                Upload Files
                              </Button>
                            </div>
                          </>
                        )}

                        {isLoggedIn && (
                          <div className="mb-3 mt-4 d-flex justify-content-center">
                            <Button type="submit" variant="primary" className="w-100" disabled={isSubmitting}>
                              Upload Files
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <StyledDropzone
                        onFileSelect={(files) => onFileSelected(files)}
                        acceptedFiles={DROPZONE_ACCEPTED_FILES_PRO}
                        showExamples={false}
                      />
                    )}
                  </form>
                )}
              </Formik>
            )}
          </div>
        </div>
        <Footer />
      </div>
    </DropzoneWrapper>
  )
}

export default TransferRequestLinkUpload
